import React from 'react';

import { AddCircleOutline, DeleteOutline } from '@mui/icons-material';
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import { ImageObject } from '../../pages/types';
import { validateHeight } from '../admin-utils';

interface CarouselEditorProps {
  images?: (string | ImageObject)[];
  carouselHeight?: string | number; // Updated to accept number as well
  onPropChange: (key: string, value: any) => void;
  openImageSelector: (index: number) => void;
  editImageIndex: number | null;
  setEditImageIndex: (index: number | null) => void;
  handleRemoveImage: (index: number) => void;
  handleUpdateImageMetadata: (
    index: number,
    field: 'title' | 'altText',
    value: string
  ) => void;
}

const CarouselEditor: React.FC<CarouselEditorProps> = ({
  images = [],
  carouselHeight,
  onPropChange,
  openImageSelector,
  editImageIndex,
  setEditImageIndex,
  handleRemoveImage,
  handleUpdateImageMetadata,
}) => {
  // Helper functions for images
  const getImageUrl = (image: string | ImageObject): string => {
    if (typeof image === 'string') return image;
    return image.url;
  };

  const getImageAlt = (image: string | ImageObject): string => {
    if (typeof image === 'string') return `Image`;
    return image.altText || `Image`;
  };

  return (
    <>
      <Typography variant="h6">Content Options</Typography>
      <List sx={{ width: '100%', alignItems: 'left' }}>
        {images.map((image, index) => (
          <ListItem
            key={index}
            component="div"
            sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
          >
            <Box display="flex" width="100%" alignItems="center">
              <img
                src={getImageUrl(image)}
                alt={getImageAlt(image)}
                style={{
                  width: '50px',
                  height: '50px',
                  marginRight: '10px',
                  objectFit: 'cover',
                }}
              />
              <ListItemButton onClick={() => openImageSelector(index)}>
                <ListItemText primary="Change Image" />
              </ListItemButton>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleRemoveImage(index)}
              >
                <DeleteOutline />
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  setEditImageIndex(editImageIndex === index ? null : index)
                }
                sx={{ ml: 1 }}
              >
                {editImageIndex === index ? 'Hide Details' : 'Edit Details'}
              </Button>
            </Box>

            {editImageIndex === index && (
              <Box sx={{ ml: 8, mt: 1, width: '100%' }}>
                <TextField
                  label="Image Title"
                  value={typeof image === 'object' ? image.title || '' : ''}
                  onChange={(e) =>
                    handleUpdateImageMetadata(index, 'title', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                />
                <TextField
                  label="Alt Text"
                  value={typeof image === 'object' ? image.altText || '' : ''}
                  onChange={(e) =>
                    handleUpdateImageMetadata(index, 'altText', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                  size="small"
                />
              </Box>
            )}
          </ListItem>
        ))}
      </List>
      <Button
        variant="contained"
        color="primary"
        onClick={() => openImageSelector(images.length)}
      >
        <AddCircleOutline />
      </Button>
      <TextField
        label="Height"
        fullWidth
        margin="normal"
        value={carouselHeight || ''}
        onBlur={(e) => {
          const value = validateHeight(e.target.value);
          onPropChange('carouselHeight', value);
        }}
        onChange={(e) => {
          onPropChange('carouselHeight', e.target.value);
        }}
      />
    </>
  );
};

export default CarouselEditor;
